import { useState, type AnchorHTMLAttributes, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Package, Send } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { useIsMobile } from '@/hooks/use-mobile';
import ContactName from '@/components/contact/ContactName';
import { supabase } from '@/integrations/supabase/client';
import { trackActivity } from '@/lib/activityTracking';
import { composeRoute, parseEntityHref } from '@/lib/entityLinks';
import { Button } from '@/components/ui/button';
import { ListingArtifactCard, ShareListingAction } from './ListingArtifact';
import { ShareMenuAction, OpenMenuShareAction } from './ShareMenuAction';

interface Counterparty { user_id: string; full_name: string; email: string | null; phone: string | null; company_name: string }

/**
 * Buyer company named by Ask Cannafax: opens its people, each through the one
 * contact card (C-UI-02). The roster itself is the same floating card on desktop
 * and the same bottom sheet as every other contact surface on a phone, so a
 * person's card never opens as a desktop popover stacked on a phone.
 */
function CompanyEntity({ id, children }: { id: string; children: ReactNode }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ['counterparty-contacts', id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_counterparty_contacts' as never, { p_company_id: id } as never);
      if (error) throw error;
      return (data as unknown as Counterparty[]) ?? [];
    },
    enabled: false,
    staleTime: 5 * 60_000,
  });

  const onOpen = (next: boolean) => {
    setOpen(next);
    if (next) {
      void q.refetch();
      trackActivity('ask_entity_opened', { kind: 'company' });
    }
  };

  // The contact card already shows each person's phone as a copyable row, so the
  // roster never repeats it: one surface, one way to reach a person.
  const roster = (
    <>
      <p className="text-sm font-semibold">{children}</p>
      {q.isFetching ? (
        <p className="mt-2 flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" />Finding their people…</p>
      ) : (q.data?.length ?? 0) === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">No contact details are shared with you for this company yet.</p>
      ) : (
        <ul className="mt-2 space-y-1.5 text-sm">
          {q.data?.map((p) => (
            <li key={p.user_id}>
              <ContactName person={{ user_id: p.user_id, full_name: p.full_name, email: p.email, phone: p.phone, company_id: id }} companyId={id} companyName={p.company_name} sourceSurface="your_desk_chat" />
            </li>
          ))}
        </ul>
      )}
    </>
  );

  const trigger = (
    <Button
      type="button"
      variant="link"
      className="inline h-auto whitespace-normal p-0 font-medium text-brand-text underline underline-offset-2"
      onClick={isMobile ? () => onOpen(true) : undefined}
    >
      {children}
    </Button>
  );

  if (isMobile) {
    return (
      <>
        {trigger}
        <Drawer open={open} onOpenChange={onOpen}>
          <DrawerContent onClick={(e) => e.stopPropagation()}>
            <div className="px-4 pb-8 pt-2">{roster}</div>
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="start">{roster}</PopoverContent>
    </Popover>
  );
}

/** Markdown link renderer for Ask Cannafax answers: listings and buyer companies become live. */
export function EntityLink({ href, children, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement>) {
  const entity = parseEntityHref(href);
  if (entity?.kind === 'menuShare') return <ShareMenuAction menuId={entity.menuId} companyId={entity.companyId}>{children}</ShareMenuAction>;
  if (entity?.kind === 'menuShareOpen') return <OpenMenuShareAction menuId={entity.menuId}>{children}</OpenMenuShareAction>;
  if (entity?.kind === 'compose') {
    return (
      <Button asChild size="sm" className="my-1 h-8 gap-1.5 no-underline">
        <Link to={composeRoute(entity.id, entity.draft)} onClick={() => trackActivity('ask_entity_opened', { kind: 'compose' })}>
          <Send className="h-3.5 w-3.5" aria-hidden />{children}
        </Link>
      </Button>
    );
  }
  if (entity?.kind === 'share') return <ShareListingAction listingId={entity.listingId} companyId={entity.companyId}>{children}</ShareListingAction>;
  if (entity?.kind === 'listingCard') return <ListingArtifactCard id={entity.id} />;
  if (entity?.kind === 'company') return <CompanyEntity id={entity.id}>{children}</CompanyEntity>;
  if (entity?.kind === 'listing') {
    return (
      <Link to={`/listings/${entity.id}`} onClick={() => trackActivity('ask_entity_opened', { kind: 'listing' })}
        className="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 font-medium text-foreground hover:bg-muted/70">
        <Package className="h-3 w-3 text-muted-foreground" aria-hidden />{children}
      </Link>
    );
  }
  if (href?.startsWith('/')) return <Link to={href} className="text-primary underline underline-offset-2">{children}</Link>;
  return <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>{children}</a>;
}
